import React, { useEffect } from 'react';

import { useAnimation } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { DEFAULT_SORT } from '@pkg/hooks';
import { NftFiltersKey, useNftFilter } from '@pkg/providers';

import { type GroupItemSorts } from '@app/client/common/structs/groups/groups';

import { SortItem } from '../styles';

import { MOTION_VARIANTS } from './GroupButtonSort.motion';

import { DirectionButton, DirectionIndicator, Wrapper } from './styles';

const SORT_ITEMS: ReadonlyArray<GroupItemSorts> = ['purchase_date', 'floor_price'];
const FILTER_KEY = NftFiltersKey.Sort;

export const GroupButtonSort = () => {
    const { t: translate } = useTranslation(['groups']);

    const sortingMotionControls = useAnimation();
    const [filters, setFilters] = useNftFilter();

    // TODO: Can remove this typecast once we have fully typed
    // `NftFilterProvider` using the available generics.
    const directionFromFilters = filters.getLastValue(FILTER_KEY);
    const directionCurrent: GroupItemSorts = directionFromFilters
        ? (directionFromFilters as GroupItemSorts)
        : DEFAULT_SORT;

    useEffect(() => {
        sortingMotionControls.start(directionCurrent === DEFAULT_SORT ? 'left' : 'right');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [directionCurrent]);

    function handleUpdateDirection(direction: GroupItemSorts) {
        // TODO: We need a way to specify array vs single value.
        if (filters.getLastValue(FILTER_KEY) === direction) {
            return;
        } else {
            filters.set(FILTER_KEY, [direction]);
            setFilters(filters);
        }
    }

    function handleClick(direction: GroupItemSorts) {
        sortingMotionControls.start(direction === DEFAULT_SORT ? 'left' : 'right');
        handleUpdateDirection(direction);
    }

    function handleTapStart() {
        sortingMotionControls.start(directionCurrent === DEFAULT_SORT ? 'leftTapping' : 'rightTapping');
    }

    function handleMouseLeave() {
        sortingMotionControls.start(directionCurrent === DEFAULT_SORT ? 'left' : 'right');
    }

    return (
        <Wrapper>
            <DirectionIndicator
                animate={sortingMotionControls}
                initial={directionCurrent === DEFAULT_SORT ? 'left' : 'right'}
                variants={MOTION_VARIANTS}
                transition={{ duration: 0.3, ease: 'easeOut' }}
            />

            {SORT_ITEMS.map((sort) => (
                <DirectionButton
                    key={sort}
                    size="small"
                    color="clear"
                    variant="solid"
                    // TODO: This should come from `i18n`.
                    aria-label={`Select ${sort}`}
                    onTapStart={handleTapStart}
                    onClick={() => handleClick(sort)}
                    onMouseLeave={handleMouseLeave}
                >
                    <SortItem>{translate(`groups_sort_${sort}`)}</SortItem>
                </DirectionButton>
            ))}
        </Wrapper>
    );
};
